/**
 * The Additions tab (PLAN-ADDITIONS-PROFILES).
 *
 * Mostly read-only, like the schedule tabs: the misc table on a recipe is where
 * these are made, and a second row editor here would drift from it. The detail pane
 * says what batch size the amounts were written for, because applying scales
 * them from that.
 *
 * The one thing editable here is the list of styles, which only decides where
 * the misc table's menu lists the profile. Saving from a recipe fills it with
 * that recipe's style, and a brewer whose house list suits three styles adds
 * the other two here.
 */

import { getStyle, styleCode } from '@beerwright/catalog';
import type { MiscProfileDoc, RecipeMisc } from '@beerwright/commands';
import { XIcon } from 'lucide-react';
import { ProfileLibraryTab } from '@/components/profiles/profile-library-tab';
import { AddStylePicker } from '@/components/recipe/style-picker';
import { formatCount } from '@/lib/format';
import { MISC_USE_LABELS } from '@/lib/recipe-labels';
import { formatMinutes, formatQuantityWithUnit, trimZeros } from '@/lib/units';
import { useDispatch } from '@/store/use-dispatch';
import { useUnits } from '@/store/workspace-context';

const MAX_STYLES = 20;

/** The amount the way the misc table writes it: tablets as a count, the rest in g or mL. */
function formatMiscAmount(item: RecipeMisc): string {
  if (item.unit === 'each') return `${formatCount(item.amount)} each`;
  return `${trimZeros(item.amount.toFixed(2))} ${item.unit === 'ml' ? 'mL' : 'g'}`;
}

/**
 * A style id as a reader knows it, `21A American IPA`. An id the catalog no
 * longer has prints as itself.
 */
function styleLabel(styleId: string): string {
  const style = getStyle(styleId);
  return style === undefined ? styleId : `${styleCode(style)} ${style.name}`;
}

/**
 * The profile's styles as removable chips, and the control that adds one.
 * Each change is one `miscProfile.update` carrying the whole new list.
 */
function ProfileStyles({ profile }: { readonly profile: MiscProfileDoc }) {
  const { dispatch } = useDispatch();
  const setStyleIds = (styleIds: string[]) =>
    dispatch('miscProfile.update', { profileId: profile.id, changes: { styleIds } });

  return (
    <section className="flex flex-col gap-2" aria-label="Styles">
      {profile.styleIds.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Not tied to a style. It's listed under Your profiles on every recipe.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {profile.styleIds.map((styleId) => {
            const label = styleLabel(styleId);
            return (
              <li
                key={styleId}
                className="flex items-center gap-1 rounded-full border border-border py-0.5 pr-1 pl-2.5 text-xs"
              >
                <span>{label}</span>
                <button
                  type="button"
                  aria-label={`Remove ${label}`}
                  onClick={() => setStyleIds(profile.styleIds.filter((id) => id !== styleId))}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <XIcon className="size-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {/* The schema holds twenty, which is more than any house list needs. */}
      {profile.styleIds.length < MAX_STYLES && (
        <div>
          <AddStylePicker
            styleIds={profile.styleIds}
            onAdd={(styleId) => {
              if (profile.styleIds.includes(styleId)) return;
              setStyleIds([...profile.styleIds, styleId]);
            }}
          />
        </div>
      )}
    </section>
  );
}

export function MiscProfilesTab({ profiles }: { readonly profiles: readonly MiscProfileDoc[] }) {
  const units = useUnits();
  return (
    <ProfileLibraryTab
      profiles={profiles}
      domain="miscProfile"
      noun="additions profile"
      contents="additions"
      emptyMessage="No additions profiles yet. Set up the misc additions on a recipe and choose Save as profile."
      summary={(profile) =>
        profile.miscs.length === 1 ? '1 addition' : `${profile.miscs.length} additions`
      }
      detail={(selected) => {
        return (
          <>
            <p className="text-muted-foreground text-sm">
              Written for {formatQuantityWithUnit(selected.batchVolumeL, 'volume', units)}.
            </p>

            <ProfileStyles profile={selected} />

            <ol className="divide-y divide-border overflow-hidden rounded-lg border border-border">
              {selected.miscs.map((item) => (
                <li
                  key={item.itemId}
                  className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span>{item.name}</span>
                  <span className="flex gap-3 font-mono text-muted-foreground text-xs tabular-nums">
                    <span>{MISC_USE_LABELS[item.use]}</span>
                    <span>{formatMiscAmount(item)}</span>
                    <span>{item.timeMinutes === null ? '—' : formatMinutes(item.timeMinutes)}</span>
                  </span>
                </li>
              ))}
            </ol>
          </>
        );
      }}
    />
  );
}
